import { useMemo, type JSX } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Select from '../../ui/Select.js'

const R = 'rounded-[3px]'

export const CLIENTS_PAGE_SIZE_OPTIONS = [10, 20, 50]

export interface ClientsPaginationBarProps {
  page: number
  pageSize: number
  total: number
  /** Noun shown in "Showing x–y of N <noun>" — defaults to "clients". */
  noun?: string
  onPageChange: (p: number) => void
  onPageSizeChange: (size: number) => void
}

/* ── Footer / pagination — shared by the Clients and Customer 360 lists ── */
export default function ClientsPaginationBar({ page, pageSize, total, noun = 'clients', onPageChange, onPageSizeChange }: ClientsPaginationBarProps): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = []
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i) }
    else if (page <= 3) pages.push(1, 2, 3, 4, '…', totalPages)
    else if (page >= totalPages - 2) pages.push(1, '…', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
    else pages.push(1, '…', page - 1, page, page + 1, '…', totalPages)
    return pages
  }, [page, totalPages])

  const navBtn = `flex h-7 w-7 items-center justify-center ${R} border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-40`

  return (
    <div className="flex h-12 flex-wrap items-center justify-between gap-4 border-t border-slate-200 bg-white px-6 text-xs text-slate-600">
      <span>
        Showing <strong className="font-mono font-semibold text-slate-800">{start}–{end}</strong> of <strong className="font-mono font-semibold text-slate-800">{total.toLocaleString()}</strong> {noun}
      </span>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-slate-500">
          Rows per page:
          <Select
            ariaLabel="Rows per page"
            size="sm"
            value={String(pageSize)}
            onChange={(v) => onPageSizeChange(Number(v))}
            options={CLIENTS_PAGE_SIZE_OPTIONS.map((s) => ({ value: String(s), label: String(s) }))}
            className="w-[64px]"
          />
        </label>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1} className={navBtn} title="Previous page"><ChevronLeft className="h-4 w-4" /></button>
          {pageNumbers.map((p, i) => p === '…'
            ? <span key={`gap-${i}`} className="px-1 text-slate-300">…</span>
            : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p as number)}
                className={`${R} border px-2.5 py-1 font-mono text-xs transition-colors ${page === p ? 'border-blue-300 bg-blue-50 font-semibold text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >{p}</button>
            ))}
          <button type="button" onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className={navBtn} title="Next page"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  )
}
